import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, resolveDirective as _resolveDirective, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-17aaaa50"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "security-event-distribution-chart-wrap" }
const _hoisted_2 = { class: "chart-container" }

import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import * as echarts from 'echarts'
import { ResizeService } from '@/services/resize.service'
import { getAssignPieOption } from '@/utils/echarts-option/pie'
import { getSecurityEventPiePalette } from '@/utils/chart-theme-colors'
import { useEchartsThemeSync } from '@/composables/use-echarts-theme-sync'
import Card from '@/views/dashboard/_components/card/card.vue'
import PieCenter from '@/views/dashboard/_components/echarts/pie-center.vue'
import EmptyData from '@/views/dashboard/_components/empty-data/empty-data.vue'
import TimeRangeSelect from '@/views/dashboard/_components/time-range-select/time-range-select.vue'
import { TimeRange } from '@/views/dashboard/_components/time-range-select/time-range.enum'
import { getTimeRangeByEnum } from '@/utils/time'
import { SecurityEventService } from '../security-event.service'
import { DistributionDimension } from '../constants/enums'
import { securityEventLevelPieColor, trAttackTactic, trEventLevel, trEventType } from '../labels'
import type { SecurityEventLevel, SecurityEventType } from '../constants/enums'


export default /*#__PURE__*/_defineComponent({
  __name: 'security-event-distribution-chart',
  props: {
    dimension: {},
    title: {},
    hasPermission: { type: Boolean }
  },
  emits: ["sliceClick", "timeChange"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const { t } = useI18n()
const injector = useReflectiveInjector([SecurityEventService, ResizeService])
const securityEventService = injector.get(SecurityEventService)
const resizeService = injector.get(ResizeService)

const isLoading = ref(false)
const timeRange = ref<TimeRange>(TimeRange.DAYS_7)
const rawItems = ref<Array<{ key: string | number; count: number }>>([])

const chartRef = ref<HTMLElement>()
let chartInstance: echarts.EChartsType | null = null

function resolveFilterValue (key: string | number): string | number {
  if (props.dimension === DistributionDimension.AttackStage) {
    return String(key)
  }
  const num = Number(key)
  return Number.isFinite(num) ? num : key
}

function labelForKey (key: string | number): string {
  if (props.dimension === DistributionDimension.Level) {
    return trEventLevel(t, Number(key) as SecurityEventLevel)
  }
  if (props.dimension === DistributionDimension.Category) {
    return trEventType(t, key as SecurityEventType)
  }
  return trAttackTactic(t, key)
}

const chartData = computed(() =>
  rawItems.value.map((item) => ({
    name: labelForKey(item.key),
    value: item.count,
    filterValue: resolveFilterValue(item.key),
  }))
)

const totalCount = computed(() =>
  chartData.value.reduce((sum, item) => sum + item.value, 0)
)

const hasData = computed(() => totalCount.value > 0)
const colors = computed(() => getSecurityEventPiePalette(chartData.value.length || 6))
const eventCountUnit = computed(() => t('securityEvent.eventCountUnit'))
const pieCenterEventCountUnit = computed(() => {
  const unit = eventCountUnit.value
  return /^[A-Za-z]/.test(unit) ? ` ${unit}` : unit
})

function getSliceColor (item: { filterValue: string | number }, idx: number) {
  if (props.dimension === DistributionDimension.Level) {
    return securityEventLevelPieColor(Number(item.filterValue))
  }
  const palette = colors.value
  return palette[idx % palette.length]
}

function getChartData () {
  return chartData.value.map((item, idx) => ({
    value: item.value,
    name: item.name,
    filterValue: item.filterValue,
    itemStyle: { color: getSliceColor(item, idx) },
  }))
}

function bindChartClick () {
  chartInstance?.off('click')
  chartInstance?.on('click', (params: unknown) => {
    const p = params as {
      componentType?: string
      data?: { filterValue?: string | number }
      dataIndex?: number
    }
    if (p.componentType !== 'series') return
    let v = p.data?.filterValue
    if (v == null && p.dataIndex != null) {
      v = chartData.value[p.dataIndex]?.filterValue
    }
    if (v != null) emit('sliceClick', props.dimension, v)
  })
}

function createChart () {
  if (!chartRef.value) return
  chartInstance = echarts.init(chartRef.value)
  bindChartClick()
}

function getChartOption () {
  const option = {
    series: [{
      name: props.title,
      data: getChartData(),
      cursor: 'pointer',
    }],
  }

  return getAssignPieOption(option, { unit: eventCountUnit.value })
}

function renderChart () {
  if (!chartInstance) return
  chartInstance.setOption(getChartOption())
  bindChartClick()
}

function initChart () {
  chartInstance?.dispose()
  createChart()
  renderChart()
}

function loadData () {
  if (!props.hasPermission) {
    rawItems.value = []
    return
  }
  isLoading.value = true
  const [start, end] = getTimeRangeByEnum(timeRange.value)

  securityEventService.getDistribution({
    dimension: props.dimension,
    start_time: start,
    end_time: end,
  }).then(({ items }) => {
    rawItems.value = items || []
  }).finally(() => {
    isLoading.value = false
  })
}

function onTimeChange () {
  emit('timeChange', timeRange.value)
  loadData()
}

const resizeSubscribe = resizeService.resizeSubject.subscribe(() => {
  chartInstance?.resize()
})

watch(chartData, () => {
  if (!hasData.value) return
  if (!chartInstance) {
    nextTick(initChart)
  } else {
    renderChart()
  }
}, { deep: true })

watch(() => props.dimension, () => loadData())

watch(() => props.hasPermission, () => loadData())

useEchartsThemeSync(() => {
  if (chartInstance && hasData.value) renderChart()
})

onMounted(() => {
  loadData()
  if (hasData.value) {
    setTimeout(() => initChart(), 100)
  }
})

onUnmounted(() => {
  resizeSubscribe.unsubscribe()
  chartInstance?.dispose()
})

return (_ctx: any,_cache: any) => {
  const _directive_loading = _resolveDirective("loading")!

  return _withDirectives((_openBlock(), _createBlock(Card, {
    size: "second",
    title: _ctx.title,
    class: "chart-card"
  }, {
    headerRight: _withCtx(() => [
      _createVNode(TimeRangeSelect, {
        modelValue: timeRange.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((timeRange).value = $event)),
        disabled: !_ctx.hasPermission,
        onChange: onTimeChange
      }, null, 8, ["modelValue", "disabled"])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _withDirectives(_createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", {
            ref_key: "chartRef",
            ref: chartRef,
            class: "chart"
          }, null, 512),
          _createVNode(PieCenter, {
            title: _ctx.$t('examinationTable.pieCenterTotal'),
            value: totalCount.value,
            unit: pieCenterEventCountUnit.value
          }, null, 8, ["title", "value", "unit"])
        ], 512), [
          [_vShow, hasData.value]
        ]),
        _withDirectives(_createVNode(EmptyData, {
          height: "100%",
          "bg-color": "transparent"
        }, null, 512), [
          [_vShow, !hasData.value]
        ])
      ])
    ]),
    _: 1
  }, 8, ["title"])), [
    [_directive_loading, isLoading.value]
  ])
}
}

})